import { Link } from 'react-router-dom';
import { useSession } from '../context/SessionContext.jsx';
import { useAllUsers, useUser } from '../hooks/useUsers';
import ProfileCard from '../components/ProfileCard.jsx';
import FollowButton from '../components/FollowButton.jsx';
import Donut from '../components/Donut.jsx';
import HoldingItem from '../components/HoldingItem.jsx';
import StorefrontLink from '../components/StorefrontLink.jsx';
import { assetKeyOf, formatDate, holdersOf, holdingColor, pieLabel } from '../utils/portfolio';
import './ProfilePage.css';

/**
 * Reusable profile for any member.
 *   <ProfilePage uid="emily" />      fetches the user
 *   <ProfilePage user={userObj} />   uses data you already have (no fetch)
 * Shows Follow for other people, and Edit / Update for your own profile.
 */
export default function ProfilePage({ uid, user: userProp }) {
  const { currentUid } = useSession();
  const { user: fetched, loading } = useUser(userProp ? null : uid);
  const { users } = useAllUsers(); // for "Also held by…"
  const user = userProp || fetched;

  if (!user) {
    return (
      <main className="page">
        <p className="muted">{loading ? 'Loading…' : 'We couldn’t find that member.'}</p>
      </main>
    );
  }

  const isMe = user.uid === currentUid;
  const firstName = user.name.split(' ')[0];
  const holdings = user.portfolio?.holdings || [];

  const actions = isMe
    ? <Link to="/me/edit" className="btn btn--ghost">Edit profile</Link>
    : <FollowButton uid={user.uid} name={firstName} block />;

  return (
    <main className="page">
      <Link to="/" className="back-link">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 18l-6-6 6-6" /></svg>
        Home
      </Link>

      <div className="profile">
        <div className="profile__side">
          <ProfileCard user={user} actions={actions} />
          <StorefrontLink user={user} isMe={isMe} />
        </div>

        <section className="card profile__portfolio" aria-labelledby="alloc-title">
          <div className="profile__alloc-head">
            <Donut holdings={holdings} size={160} ring={24} label={pieLabel(user.name, holdings)}>
              <span className="donut__big">{holdings.length}</span>
              <span className="donut__small">holdings</span>
            </Donut>
            <div className="profile__alloc-text">
              <p className="eyebrow">Portfolio</p>
              <h2 id="alloc-title" className="profile__alloc-title">
                {isMe ? 'My current allocation' : `${firstName}'s current allocation`}
              </h2>
              {user.portfolio?.updatedAt && (
                <span className="small muted">Updated {formatDate(user.portfolio.updatedAt)}</span>
              )}
              {isMe && <Link to="/me/portfolio" className="btn btn--primary profile__update">Update portfolio</Link>}
            </div>
          </div>

          {holdings.length === 0 ? (
            <p className="empty-note">
              {isMe
                ? 'You haven’t added your holdings yet.'
                : `${firstName}'s holdings and reasoning will appear here soon.`}
            </p>
          ) : (
            <ol className="profile__holdings">
              {holdings.map((h, i) => (
                <HoldingItem
                  key={`${h.ticker || h.label}-${i}`}
                  holding={h}
                  color={holdingColor(h, i)}
                  alsoHeldBy={holdersOf(users, assetKeyOf(h), user.uid, currentUid)}
                />
              ))}
            </ol>
          )}
        </section>
      </div>
    </main>
  );
}
